import { DateTime } from "luxon";
import {
  addDays,
  IanaTimezone,
  isoDate,
  LocalDateTime,
  localDateOf,
  localEndBound,
  localStartBound,
} from "@visioni/a3s-dates";

/**
 * Wall clocks in a named timezone, as the Patriot tools filters carry them.
 *
 * A filter means a time where the camera was — 02:00 in Perth, not 02:00
 * wherever the viewer happens to be — so every bound is read through an
 * explicit IANA zone and the viewer's own zone is never consulted.
 *
 * Around a clock change a range is read as widely as it can be (see
 * `localStartBound`), so a time the clocks skip or repeat never drops records
 * at the edge of a range.
 */

interface WallClock {
  readonly day: string;
  /** `HH:mm`; absent for a whole day. */
  readonly time?: string;
}

/** `YYYY-MM-DD`, or `YYYY-MM-DDTHH:mm` once a time narrows it; anything else is not a wall clock. */
function parseWallClock(text: string): WallClock | undefined {
  if (isoDate.safeParse(text).success) return { day: text };
  if (LocalDateTime.safeParse(text).success) return { day: text.slice(0, 10), time: text.slice(11) };
  return undefined;
}

/** Whether `timeZone` is an IANA zone this runtime knows. */
export const isTimeZone = (timeZone: string): boolean => IanaTimezone.safeParse(timeZone).success;

/** The first instant a `from` value covers in `timeZone` — a whole day starts at midnight. */
export function wallClockStart(text: string, timeZone: string): string | undefined {
  const clock = parseWallClock(text);
  if (clock === undefined) return undefined;
  return localStartBound(`${clock.day}T${clock.time ?? "00:00"}`, timeZone).toISOString();
}

/**
 * The last instant a `to` value covers in `timeZone`, inclusive: the end of
 * the day, or of the named minute, so "to 02:30" includes 02:30:45.
 *
 * A millisecond before the *next* boundary, read from the calendar rather than
 * by adding a fixed length, so a day the clocks change on is still covered whole.
 */
export function wallClockEnd(text: string, timeZone: string): string | undefined {
  const clock = parseWallClock(text);
  if (clock === undefined) return undefined;
  const next =
    clock.time === undefined ?
      `${addDays(clock.day, 1)}T00:00`
    : DateTime.fromISO(`${clock.day}T${clock.time}`, { zone: "UTC" })
        .plus({ minutes: 1 })
        .toFormat("yyyy-MM-dd'T'HH:mm");
  return new Date(localEndBound(next, timeZone).getTime() - 1).toISOString();
}

/** The calendar day `timeZone` shows at `at`, as `YYYY-MM-DD`. */
export const dayIn = (at: Date, timeZone: string): string => localDateOf(at, timeZone);

/** The day `days` before the day a wall clock falls on, as `YYYY-MM-DD`. */
export function daysBefore(text: string, days: number): string | undefined {
  const clock = parseWallClock(text);
  return clock === undefined ? undefined : addDays(clock.day, -days);
}
